import React from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import {
  User, Edit, GraduationCap, Briefcase, Star, Sparkles,
  Languages, Wrench, Heart, CheckCircle, TrendingUp
} from 'lucide-react';
import CandidateCategoryTags from './CandidateCategoryTags';

const CandidateFicheTab = ({
  candidate,
  analysis,
  updating,
  updateStatus,
  startEditing,
  candidateId,
  onCategoriesUpdated,
  meetings,
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6">

          {/* Résumé */}
          <Card className="p-4 md:p-6 border-tp-border">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
                <h3 className="text-base md:text-lg font-display font-semibold text-tp-text">Résumé du profil</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => startEditing('summary')} className="text-tp-text-light hover:text-tp-primary" data-testid="edit-summary-btn">
                <Edit className="h-4 w-4" />
              </Button>
            </div>
            {analysis?.summary ? (
              <p className="text-tp-text-light leading-relaxed">{analysis.summary}</p>
            ) : (
              <p className="text-sm text-tp-text-light italic">Non renseigné - Cliquez sur l'icône pour ajouter</p>
            )}
          </Card>

          {/* Formation */}
          <Card className="p-4 md:p-6 border-tp-border">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2">
                <GraduationCap className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
                <h3 className="text-base md:text-lg font-display font-semibold text-tp-text">Formation</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => startEditing('education')} className="text-tp-text-light hover:text-tp-primary">
                <Edit className="h-4 w-4" />
              </Button>
            </div>
            {analysis?.education?.length > 0 ? (
              <div className="space-y-4">
                {analysis.education.map((edu, index) => (
                  <div key={`edu-${edu.degree}-${index}`} className="border-l-2 border-tp-accent pl-4">
                    <p className="font-medium text-tp-text">{edu.degree}</p>
                    <p className="text-sm text-tp-text-light">{edu.institution}</p>
                    <p className="text-xs text-tp-text-light">{edu.year}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-tp-text-light italic">Non renseigné</p>
            )}
          </Card>

          {/* Expérience */}
          <Card className="p-4 md:p-6 border-tp-border">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <Briefcase className="h-4 w-4 md:h-5 md:w-5 text-tp-accent flex-shrink-0" />
                <h3 className="text-sm sm:text-base md:text-lg font-display font-semibold text-tp-text">Expérience pro.</h3>
                <Badge variant="secondary" className="ml-auto flex-shrink-0">
                  {candidate.years_of_experience} ans
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={() => startEditing('experience')} className="text-tp-text-light hover:text-tp-primary">
                <Edit className="h-4 w-4" />
              </Button>
            </div>
            {analysis?.work_experience?.length > 0 ? (
              <div className="space-y-4">
                {analysis.work_experience.map((exp, index) => (
                  <div key={`exp-${exp.company}-${index}`} className="border-l-2 border-tp-primary-light pl-4">
                    <p className="font-medium text-tp-text">{exp.title}</p>
                    <p className="text-sm text-tp-primary-light font-medium">{exp.company}</p>
                    <p className="text-xs text-tp-text-light mb-2">{exp.duration}</p>
                    <p className="text-sm text-tp-text-light">{exp.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-tp-text-light italic">Non renseigné</p>
            )}
          </Card>

          {/* Compétences */}
          <Card className="p-4 md:p-6 border-tp-border">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
                <h3 className="text-base md:text-lg font-display font-semibold text-tp-text">Compétences</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => startEditing('skills')} className="text-tp-text-light hover:text-tp-primary">
                <Edit className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-4">
              {analysis?.skills?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-tp-text-light mb-2">Compétences principales</p>
                  <div className="flex flex-wrap gap-2">
                    {analysis.skills.map((skill) => (
                      <Badge key={`skill-${skill}`} className="bg-tp-accent/10 text-tp-accent border-0">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {candidate.secondary_skills && (
                <div>
                  <p className="text-xs font-medium text-tp-text-light mb-2">Compétences secondaires</p>
                  <p className="text-sm text-tp-text">{candidate.secondary_skills}</p>
                </div>
              )}

              {!analysis?.skills?.length && !candidate.secondary_skills && (
                <p className="text-sm text-tp-text-light italic">Non renseigné</p>
              )}
            </div>
          </Card>
        </div>

        {/* Right Column - Quick Info */}
        <div className="space-y-4 md:space-y-6">
          {/* Multi-category tags (editable) */}
          <CandidateCategoryTags
            candidate={candidate}
            candidateId={candidateId}
            onUpdated={onCategoriesUpdated}
          />

          {/* Conversion Score (real-usage based) */}
          {(candidate.conversion_score !== undefined && candidate.conversion_score !== null) && (
            <Card className="p-4 md:p-6 border-tp-border" data-testid="conversion-score-card">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
                  <h3 className="text-sm font-semibold text-tp-text">Score de conversion</h3>
                </div>
                <span
                  className={`text-lg font-bold ${
                    candidate.conversion_score >= 70 ? 'text-green-600' :
                    candidate.conversion_score >= 50 ? 'text-tp-primary' :
                    'text-gray-400'
                  }`}
                  data-testid="conversion-score-value"
                >
                  {candidate.conversion_score}/100
                </span>
              </div>
              <Progress value={candidate.conversion_score} className="h-2 mb-3" />
              {candidate.conversion_stats && (
                <div className="grid grid-cols-2 gap-2 text-xs text-tp-text-light">
                  <div>Présenté : <span className="font-medium text-tp-text">{candidate.conversion_stats.presented || 0}</span></div>
                  <div>Intéressé : <span className="font-medium text-tp-text">{candidate.conversion_stats.interested || 0}</span></div>
                  <div>Embauché : <span className="font-medium text-green-600">{candidate.conversion_stats.hired || 0}</span></div>
                  <div>Rejeté : <span className="font-medium text-red-500">{candidate.conversion_stats.rejected || 0}</span></div>
                </div>
              )}
              <p className="text-[10px] text-gray-400 mt-2 italic">Basé sur l'historique réel de décisions clients</p>
            </Card>
          )}

          {/* AI Enrichment — supprimé : doublon avec CandidateAIBrief affiché en haut de la fiche */}

          {/* Score */}
          {analysis && (
            <Card className="p-4 md:p-6 border-tp-border">
              <h3 className="text-sm font-semibold text-tp-text mb-4">Score candidat</h3>
              <div className="text-center mb-4">
                <div className={`text-4xl font-display font-bold ${
                  analysis.candidate_score >= 70 ? 'text-tp-accent' :
                  analysis.candidate_score >= 50 ? 'text-tp-primary' :
                  'text-tp-primary/60'
                }`}>
                  {analysis.candidate_score}
                </div>
                <p className="text-xs text-tp-text-light">/ 100</p>
              </div>
              <Progress value={analysis.candidate_score} className="h-2 mb-2" />
              <p className="text-xs text-tp-text-light text-center capitalize">
                {analysis.classification}
              </p>
            </Card>
          )}

          {/* Langues */}
          <Card className="p-4 md:p-6 border-tp-border">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2">
                <Languages className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
                <h3 className="text-sm font-semibold text-tp-text">Langues</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => startEditing('languages')} className="text-tp-text-light hover:text-tp-primary h-6 w-6 p-0">
                <Edit className="h-3 w-3" />
              </Button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-tp-text">Français</span>
                <Badge variant="secondary" className="capitalize text-xs">
                  {candidate.french_level === 'native' ? 'Natif' :
                   candidate.french_level === 'fluent' ? 'Courant' :
                   candidate.french_level === 'intermediate' ? 'Intermédiaire' :
                   'Basique'}
                </Badge>
              </div>
              {analysis?.languages?.filter(lang =>
                !['français', 'french', 'francais'].includes((lang.language || '').toLowerCase())
              ).map((lang, index) => (
                <div key={`lang-${lang.language}-${index}`} className="flex items-center justify-between">
                  <span className="text-sm text-tp-text">{lang.language}</span>
                  <Badge variant="secondary" className="text-xs">{lang.level}</Badge>
                </div>
              ))}
            </div>
          </Card>

          {/* Outils maîtrisés */}
          <Card className="p-4 md:p-6 border-tp-border">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
                <h3 className="text-sm font-semibold text-tp-text">Outils maîtrisés</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => startEditing('tools')} className="text-tp-text-light hover:text-tp-primary h-6 w-6 p-0">
                <Edit className="h-3 w-3" />
              </Button>
            </div>
            {analysis?.tools?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {analysis.tools.map((tool) => (
                  <Badge key={`tool-${tool}`} variant="outline" className="text-xs border-tp-border">
                    {tool}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-tp-text-light italic">Non renseigné</p>
            )}
          </Card>

          {/* Qualités / Points forts */}
          <Card className="p-4 md:p-6 border-tp-border">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
                <h3 className="text-sm font-semibold text-tp-text">Points forts</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => startEditing('strengths')} className="text-tp-text-light hover:text-tp-primary h-6 w-6 p-0">
                <Edit className="h-3 w-3" />
              </Button>
            </div>
            {analysis?.strengths?.length > 0 ? (
              <ul className="space-y-2">
                {analysis.strengths.map((strength, index) => (
                  <li key={`strength-${strength}-${index}`} className="text-sm flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-tp-success mt-0.5 flex-shrink-0" />
                    <span className="text-tp-text">{strength}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-tp-text-light italic">Non renseigné</p>
            )}
          </Card>

          {/* Prérequis */}
          <Card className="p-4 md:p-6 border-tp-border">
            <h3 className="text-sm font-semibold text-tp-text mb-3 md:mb-4">Prérequis techniques</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-tp-text-light">Ordinateur</span>
                <span className={candidate.has_computer ? 'text-tp-accent font-medium' : 'text-tp-text-light'}>
                  {candidate.has_computer ? '✓ Oui' : '— Non'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-tp-text-light">Internet stable</span>
                <span className={candidate.has_internet ? 'text-tp-accent font-medium' : 'text-tp-text-light'}>
                  {candidate.has_internet ? '✓ Oui' : '— Non'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-tp-text-light">Disponibilité</span>
                <span className="text-tp-text capitalize">
                  {candidate.availability === 'full-time' ? 'Temps plein' : 'Temps partiel'}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
  );
};

export default CandidateFicheTab;
